import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import cx from 'classnames';
import moment from 'moment';

import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { BlockedByFeature } from '../../../featureToggles/features/utils';
import {
  AccountStates,
  IAccount,
  IIdentifier,
  ManagerDetails,
  UpgradeStatus
} from '../../../slices/accounts/account';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { editAccount, ICreateAccountDTO } from '../../../slices/accounts/form.thunks';
import { selectors } from '../../../slices/accounts/hubspotAccounts';
import { fetchHubspotAccountById } from '../../../slices/accounts/hubspotAccounts.thunks';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { RootState } from '../../../slices/rootReducer';
import { ConfirmModal } from '../../common/plgExternal/confirmModal';
import { AccountFeatures } from './accountFeatures';
import { EnterpriseUpgrade } from './upgrades/enterpriseUpgrade';
import { SxTooltip } from '../../common/utils/wrapComponents';
import { selectors as authSelectors } from '../../../slices/auth';
import { UserRoles } from '../../common/domain/userRoles';
import { startEnterpriseTrial } from '../../../slices/packagesInfo/index.thunk';
import { ITrial } from '../../../slices/packagesInfo';
import { isExternalClient } from '../../../utils/clients';

import styles from './settings.module.scss';

const isTrialsOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);
const UPGRADE_IN_PROGRESS_MESSAGE: Readonly<string> = 'The account has an upgrade in progress';
const HUBSPOT_COMPANY_ID = 'Hubspot company ID';
const ACCOUNT_MANAGER = 'Account manager';
const CSM = 'CSM';
const RENEWAL_DATE = 'Renewal Date';

interface IState {
  isDeactivateModalOpen: boolean;
  isUpgradeToEnterpriseModalOpen: boolean;
}

interface IMapStateToProps {
  account: IAccount | typeof BlockedByFeature | null;
  loading: boolean;
  accountId: string;
  hubspotAccountId: string;
  // use for Features.TrialOfTiers
  canStartEnterpriseTrial: boolean;
  hasPastEnterpriseTrial: boolean;
  hasActiveTrial: boolean;
  currentTrial: ITrial | null;
  isUpgrading: boolean;
  // end of use for Features.TrialOfTiers
}

interface IDispatchToProps {
  editAccount: (formValues: ICreateAccountDTO) => void;
  fetchHubspotAccountById: (id: string) => void;
  // use for Features.TrialOfTiers
  startEnterpriseTrial: (accountId: string) => void;
  // end of use for Features.TrialOfTiers
}

type IProps = IMapStateToProps & IDispatchToProps;

class AccountSettingsBase extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isDeactivateModalOpen: false,
      isUpgradeToEnterpriseModalOpen: false
    };
    this.closeDeactivateModal = this.closeDeactivateModal.bind(this);
    this.openDeactivateModal = this.openDeactivateModal.bind(this);
    this.onDeactivateAccount = this.onDeactivateAccount.bind(this);
    this.onActivateAccount = this.onActivateAccount.bind(this);
    this.toggleUpgradeModal = this.toggleUpgradeModal.bind(this);
    this.startEnterpriseTrial = this.startEnterpriseTrial.bind(this);
  }

  componentDidMount() {
    this.props.fetchHubspotAccountById(this.props.accountId);
  }

  private openDeactivateModal() {
    this.setState({ isDeactivateModalOpen: true });
  }

  private closeDeactivateModal() {
    this.setState({ isDeactivateModalOpen: false });
  }

  private onDeactivateAccount() {
    const { account, editAccount } = this.props;
    if (account === BlockedByFeature || account === null) {
      return;
    }

    const accountToUpdate: ICreateAccountDTO = {
      id: account.id,
      name: account.name,
      accessType: account.accessType,
      status: AccountStates.LAPSED
    };

    editAccount(accountToUpdate);
    this.closeDeactivateModal();
  }

  private onActivateAccount() {
    const { account, editAccount } = this.props;
    if (account === BlockedByFeature || account === null || account.status === AccountStates.ACTIVE) {
      return;
    }

    const accountToUpdate: ICreateAccountDTO = {
      id: account.id,
      name: account.name,
      accessType: account.accessType,
      status: AccountStates.ACTIVE
    };

    editAccount(accountToUpdate);
  }

  private renderAccountModal() {
    if (!this.state.isDeactivateModalOpen) {
      return null;
    }

    const { account } = this.props;
    if (account === BlockedByFeature || account === null || account.status === AccountStates.LAPSED) {
      return null;
    }

    return <ConfirmModal
      title="Deactivate Account"
      description={<p className={styles.description}>Are you sure you want to deactivate this account? Users will be able to log in, but will only be able to access the raw data from their projects.</p>}
      okBtnText="Deactivate"
      cancelBtnText="Cancel"
      onConfirm={this.onDeactivateAccount}
      onClose={this.closeDeactivateModal}
      visible={this.state.isDeactivateModalOpen}
    />;
  }

  private getManagerName(manager?: ManagerDetails) {
    const defaultValue = '_';
    const firstName = manager?.firstName ?? defaultValue;
    const lastName = manager?.lastName ?? defaultValue;
    return `${firstName} ${lastName}`;
  }

  private formatDate(dateString?: string): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  private toggleUpgradeModal() {
    this.setState((state) => ({ isUpgradeToEnterpriseModalOpen: !state.isUpgradeToEnterpriseModalOpen }));
  }

  private getUpgradeToEnterpriseTooltip() {
    const { account, isUpgrading } = this.props;
    if (account === BlockedByFeature || account === null) {
      return;
    }
    if (!account.billingAddress?.address.country) {
      return <span>Billing information must be populated on the <a href={`/accounts/${account.id}/billing`}>Billing tab</a> before this account can be upgraded.</span>;
    }
    if (isUpgrading) {
      return <span>{UPGRADE_IN_PROGRESS_MESSAGE}</span>;
    }
  }

  private startEnterpriseTrial() {
    const { canStartEnterpriseTrial, accountId } = this.props;
    if (!isTrialsOfTiersEnabled || !canStartEnterpriseTrial) {
      return;
    }
    this.props.startEnterpriseTrial(accountId);
  }

  private renderStartEnterpriseTrial() {
    if (!isTrialsOfTiersEnabled) {
      return null;
    }

    const { canStartEnterpriseTrial, hasActiveTrial, hasPastEnterpriseTrial, isUpgrading } = this.props;
    const button = <Button type="primary" onClick={this.startEnterpriseTrial} disabled={!canStartEnterpriseTrial}>
      <span>Start enterprise trial</span>
    </Button>;

    if (!canStartEnterpriseTrial) {
      let tooltipText;
      if (hasActiveTrial) {
        tooltipText = 'The account has a trial in progress';
      }

      if (hasPastEnterpriseTrial) {
        tooltipText = 'The account already had an enterprise trial';
      }

      if (isUpgrading) {
        tooltipText = UPGRADE_IN_PROGRESS_MESSAGE;
      }

      if (tooltipText) {
        return <SxTooltip Component={button} tooltipText={tooltipText} />;
      }
    }

    return button;
  }

  private renderTrialLabel(): ReactNode {
    const { currentTrial } = this.props;
    if (!isTrialsOfTiersEnabled || !currentTrial) {
      return null;
    }
    const trialName = currentTrial.descriptor === 'enterpriseTrial' ? 'Enterprise' : 'Professional';
    const trialEndDate = moment.utc(currentTrial.endDateUtc).format('MMMM D');
    return <div className={styles.trialLabel}>{trialName} trial ends on {trialEndDate}</div>;
  }

  private renderCompanyDetails(): ReactNode {
    const { account, hubspotAccountId, accountId } = this.props;
    const { isUpgradeToEnterpriseModalOpen } = this.state;

    if (account === BlockedByFeature || account === null) {
      return null;
    }
    const { accessType } = account;
    const firstName = account.managerDetails?.firstName ?? '_';
    const lastName = account.managerDetails?.lastName ?? '_';
    const managerName = `${firstName} ${lastName}`;
    const defaultValue = '_ _';
    const customerSuccessManager = this.getManagerName(account.customerSuccessManagerDetails);
    const infoDefinition = [
      ['SightX ID', account.id],
      [HUBSPOT_COMPANY_ID, hubspotAccountId],
      [ACCOUNT_MANAGER, managerName],
      [CSM, customerSuccessManager],
      [RENEWAL_DATE, this.formatDate(account.renewalAt?.toString())],
      ['Creation date', this.formatDate(account.createdAt?.toString())],
      ['Last login date', this.formatDate(account.lastLoginDate?.toString())],
      ['Last launch date', this.formatDate(account.lastCampaignLaunchAt?.toString())],
      ['Server region', account.region]
    ];
    const info = infoDefinition.filter((definition) => {
      const [key] = definition;
      const hiddenForExternalFields = [HUBSPOT_COMPANY_ID, ACCOUNT_MANAGER, CSM, RENEWAL_DATE];
      return !(isExternalClient() && hiddenForExternalFields.includes(`${key}`));
    });

    const lapsedAccount = account.status === AccountStates.LAPSED;
    const isEnterprise = accessType === AccessTypes.Enterprise;
    const canUpgrade = [AccessTypes.Lite, AccessTypes.Professional].includes(accessType);
    const showDeactivateButton = isEnterprise && !lapsedAccount;
    const showLapsedLabel = isEnterprise && lapsedAccount;
    const upgradeTooltip = this.getUpgradeToEnterpriseTooltip();
    const disableUpgradeToEnterprise = !!upgradeTooltip;
    return (
      <div className={cx(styles.companyDetails, styles.plgExternal3)}>
        <div className={styles.companyHeader}>
          <div>
            <div className={cx(styles.tag, {
              [styles.lite]: accessType === AccessTypes.Lite,
              [styles.enterprise]: accessType === AccessTypes.Enterprise
            })}>
              {accessType?.toLocaleLowerCase()}
            </div>
            <p className={styles.name}>{account.name}</p>
            {this.renderTrialLabel()}
            {showLapsedLabel && <p className={styles.lapsed}>Lapsed</p>}
          </div>
          {isEnterprise &&
            <Button type="default" onClick={showDeactivateButton ? this.openDeactivateModal : this.onActivateAccount}>
              {showDeactivateButton ? 'Deactivate' : 'Activate'} account
            </Button>
          }
          {canUpgrade &&
            <div className={styles.buttons}>
              {this.renderStartEnterpriseTrial()}
              <SxTooltip
                Component={
                  <Button type="default" onClick={this.toggleUpgradeModal} disabled={disableUpgradeToEnterprise}>
                    <span>Upgrade to Enterprise</span>
                  </Button>
                }
                hideTooltip={!disableUpgradeToEnterprise}
                tooltipText={upgradeTooltip}
              />
            </div>
          }
        </div>
        <div className={styles.detailsContainer}>
          {info.map(([label, value]) =>
            <div key={label}>
              <p className={styles.label}>{label}</p>
              <p className={styles.text}>{value ?? defaultValue}</p>
            </div>
          )}
        </div>
        {isUpgradeToEnterpriseModalOpen &&
          <EnterpriseUpgrade
            accountId={accountId}
            onCancel={this.toggleUpgradeModal}
            accessType={accessType}
            hubspotAccountId={hubspotAccountId}
          />
        }
      </div>
    );
  }

  render(): ReactNode {
    const { account, loading } = this.props;
    if (loading) {
      return <div className={styles.loadingContainer}>
        <LoadingOutlined />
      </div>;
    }

    if (account === BlockedByFeature || account === null) {
      return null;
    }

    return (
      <div className={styles.mainContainer}>
        {this.renderCompanyDetails()}
        <div className={styles.features}>
          <p className={styles.title}>Feature Access</p>
          <AccountFeatures
            initialValues={account}
          />
        </div>
        {this.renderAccountModal()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, props: RouteComponentProps<IIdentifier>): IMapStateToProps => {
  const { id = '' } = props.match.params;
  const account = accountListSelectors.getAccountById(state, id);
  let canStartEnterpriseTrial = false;
  let hasActiveTrial = false;
  let hasPastEnterpriseTrial = false;
  let currentTrial: ITrial | null = null;
  let isUpgrading = false;
  if (isTrialsOfTiersEnabled && account) {
    const user = authSelectors.getLoggedInUser(state);
    const isSupport = user?.roles?.some((role) => role === UserRoles.Support);
    const isSxAdmin = authSelectors.isAuthUserSightxAdmin(state);
    const [currentPackage] = account.packagesInfo?.current?.trial || [];
    currentTrial = currentPackage;
    hasActiveTrial = !!currentPackage;
    hasPastEnterpriseTrial = !!account.packagesInfo?.past?.trial?.some((trial) => trial.descriptor === 'enterpriseTrial');
    isUpgrading = account.upgradeInfo?.status === UpgradeStatus.InProgress;
    if (
      !hasActiveTrial &&
      (!hasPastEnterpriseTrial || (hasPastEnterpriseTrial && isSupport && isSxAdmin)) &&
      !isUpgrading
    ) {
      canStartEnterpriseTrial = true;
    }
  }

  return {
    account,
    loading: accountListSelectors.getLoading(state),
    accountId: id,
    hubspotAccountId: selectors.getHubspotAccount(state)?.hubspotId || '',
    canStartEnterpriseTrial,
    hasActiveTrial,
    currentTrial,
    hasPastEnterpriseTrial,
    isUpgrading
  };
};

const dispatchToProps: IDispatchToProps = {
  editAccount,
  fetchHubspotAccountById,
  startEnterpriseTrial
};

const ConnectedAccountSettings = connect(mapStateToProps, dispatchToProps)(AccountSettingsBase);

export const AccountSettings = withRouter(ConnectedAccountSettings);
